/**
 * Per-user BI settings — typed client + zustand store.
 *
 * The server holds a single JSON blob per user at `/api/bi/settings/`. We
 * mirror that blob in a zustand store so any component can read/write a
 * pref synchronously (no React Query suspense), and we debounce PATCHes
 * to the server so a flurry of toggle interactions only produces one
 * network round-trip.
 *
 * The local-storage cache in `lib/storage.ts` stays in play as a
 * boot-time fallback; we call `storage.applyServerBlob` whenever the
 * canonical blob changes so existing callers (`storage.getPrefs()`, etc.)
 * keep returning sensible values.
 */
import { useCallback, useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { apiClient, get } from '../client';
import { apiErrorMessage } from '@/lib/apiError';
import { storage, type SavedView, type UserPrefs } from '@/lib/storage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BIUserSettingsBlob {
  prefs?: Partial<UserPrefs>;
  saved_views?: SavedView[];
  default_store?: number | null;
  fav_stores?: number[];
  recent_stores?: number[];
  compare_mode?: 'previous-period' | 'previous-year' | 'none' | null;
  theme?: 'light' | 'dark';
  // future keys live here — add fields as you teach the server about them.
  [k: string]: unknown;
}

export interface BIUserSettingsResponse {
  data: BIUserSettingsBlob;
  version: number;
  updated_at: string | null;
}

const SETTINGS_URL = '/api/bi/settings/';
export const userSettingsKey = ['bi', 'user-settings'] as const;

// ---------------------------------------------------------------------------
// Raw API
// ---------------------------------------------------------------------------

export function fetchUserSettings(): Promise<BIUserSettingsResponse> {
  return get<BIUserSettingsResponse>(SETTINGS_URL);
}

export async function patchUserSettings(
  patch: BIUserSettingsBlob,
): Promise<BIUserSettingsResponse> {
  const res = await apiClient.patch<BIUserSettingsResponse>(SETTINGS_URL, {
    data: patch,
  });
  return res.data;
}

export async function putUserSettings(
  data: BIUserSettingsBlob,
  baseVersion?: number,
): Promise<BIUserSettingsResponse> {
  const res = await apiClient.put<BIUserSettingsResponse>(SETTINGS_URL, {
    data,
    ...(baseVersion != null ? { base_version: baseVersion } : {}),
  });
  return res.data;
}

export async function resetUserSettings(): Promise<BIUserSettingsResponse> {
  const res = await apiClient.delete<BIUserSettingsResponse>(SETTINGS_URL);
  return res.data;
}

// ---------------------------------------------------------------------------
// Store — single source of truth on the client.
// ---------------------------------------------------------------------------

interface SettingsState {
  /** Last-known server blob, optimistically updated on local writes. */
  blob: BIUserSettingsBlob;
  /** Server version (used for optimistic concurrency on PUT). */
  version: number;
  /** Whether we've successfully loaded from the server at least once. */
  hydrated: boolean;
  /** True while a flush is in-flight. Useful for "Saving…" indicators. */
  saving: boolean;
  /**
   * Why the last flush failed, or `null`. Surfaced in the UI: a silently failed
   * auto-save is worse than no auto-save, because the user believes their
   * preference stuck and then finds it reverted on the next device.
   */
  saveError: string | null;
  /** Wall-clock of the last successful flush, for a "saved" confirmation. */
  savedAt: number | null;
  /** Apply a fresh server response — overwrites local state and the cache. */
  setFromServer: (resp: BIUserSettingsResponse) => void;
  /** Locally merge a partial blob and schedule a debounced flush. */
  patchLocal: (partial: BIUserSettingsBlob) => void;
  /** Set the saving flag; used by the debounced flusher. */
  _setSaving: (v: boolean) => void;
  _setSaveError: (message: string | null) => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  blob: {},
  version: 0,
  hydrated: false,
  saving: false,
  saveError: null,
  savedAt: null,

  setFromServer: (resp) => {
    storage.applyServerBlob(resp.data);
    set({ blob: resp.data ?? {}, version: resp.version, hydrated: true });
    // If any local toggles happened before bootstrap finished, flush them now.
    // Defer so we never chain sync storage → notify → React updates inside
    // the same stack frame as a zustand commit (avoids Radix/ref churn).
    if (Object.keys(pendingPatch).length > 0) {
      queueMicrotask(() => void flushNow());
    }
  },

  patchLocal: (partial) => {
    let next: BIUserSettingsBlob = {};
    set((s) => {
      next = mergeBlob(s.blob, partial);
      return { blob: next };
    });
    // Apply after the settings slice commits — keeps other zustand stores
    // from seeing nested updates during this store's set().
    storage.applyServerBlob(next);
    schedulePatchFlush(partial);
  },

  _setSaving: (v) => set({ saving: v }),

  _setSaveError: (message) =>
    set(message == null ? { saveError: null, savedAt: Date.now() } : { saveError: message }),
}));

/**
 * Shallow merge at the top level — same semantics the server uses on PATCH.
 * Top-level keys whose value is `undefined` are preserved from the previous
 * blob; explicit `null` is honoured (e.g. clearing `default_store`).
 */
function mergeBlob(
  prev: BIUserSettingsBlob,
  patch: BIUserSettingsBlob,
): BIUserSettingsBlob {
  const next: BIUserSettingsBlob = { ...prev };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue;
    next[k] = v;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Debounced PATCH flusher
// ---------------------------------------------------------------------------

const FLUSH_MS = 500;
let pendingPatch: BIUserSettingsBlob = {};
let flushTimer: number | null = null;
let inFlight: Promise<void> | null = null;

function schedulePatchFlush(partial: BIUserSettingsBlob) {
  pendingPatch = mergeBlob(pendingPatch, partial);
  if (flushTimer != null) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushNow();
  }, FLUSH_MS);
}

/**
 * Flush any buffered patch immediately (e.g. on logout / page hide). Resolves
 * once the in-flight write settles.
 */
export async function flushUserSettings(): Promise<void> {
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  await flushNow();
}

async function flushNow(): Promise<void> {
  if (inFlight) {
    // Coalesce: wait for the current write, then flush whatever piled up.
    await inFlight;
    if (Object.keys(pendingPatch).length === 0) return;
  }
  if (Object.keys(pendingPatch).length === 0) return;

  // Don't even attempt a network write before the bootstrap GET resolves —
  // we'd otherwise race a 401 against the AuthProvider on cold boot. The
  // patches stay buffered in `pendingPatch` and `setFromServer` will trigger
  // the next flush automatically.
  if (!useSettingsStore.getState().hydrated) return;

  const payload = pendingPatch;
  pendingPatch = {};
  const store = useSettingsStore.getState();
  store._setSaving(true);
  inFlight = (async () => {
    try {
      const resp = await patchUserSettings(payload);
      // Server is canonical — re-apply (in case other tabs/devices wrote too).
      useSettingsStore.getState().setFromServer(resp);
      useSettingsStore.getState()._setSaveError(null);
    } catch (err) {
      // On failure, keep the optimistic local state but re-queue the patch
      // so the next interaction (or a manual `flushUserSettings`) retries.
      pendingPatch = mergeBlob(payload, pendingPatch);
      useSettingsStore
        .getState()
        ._setSaveError(apiErrorMessage(err, 'Your preference is saved on this device only.'));
    } finally {
      useSettingsStore.getState()._setSaving(false);
      inFlight = null;
    }
  })();
  await inFlight;
}

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

/**
 * Fetch (and lazily create) the per-user settings blob. Pipes the response
 * into the zustand store so other components stay in sync.
 *
 * `enabled` should be set to `isAuthenticated` by the caller — there's no
 * point hitting `/api/bi/settings/` before login.
 */
export function useUserSettings(opts: { enabled?: boolean } = {}): UseQueryResult<BIUserSettingsResponse> {
  const setFromServer = useSettingsStore((s) => s.setFromServer);
  const query = useQuery({
    queryKey: userSettingsKey,
    queryFn: fetchUserSettings,
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data) setFromServer(query.data);
  }, [query.data, setFromServer]);

  return query;
}

/**
 * Imperative setter that callers use to update a top-level blob key.
 * Optimistic + debounced. Same shape as `storage.setPrefs` etc.
 */
export function useSettingsPatch() {
  const patchLocal = useSettingsStore((s) => s.patchLocal);
  return useCallback(
    (partial: BIUserSettingsBlob) => patchLocal(partial),
    [patchLocal],
  );
}

/** Convenience selector — current `prefs` slice merged with sane defaults. */
export function useUserPrefs(): UserPrefs {
  return useSettingsStore(
    useShallow((s) => {
      const fromServer = (s.blob.prefs ?? {}) as Partial<UserPrefs>;
      return { ...storage.getPrefs(), ...fromServer };
    }),
  );
}
